import { useState } from 'react';
import { createSpot, updateSpot, uploadSpotPhoto } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { parseGoogleMapsUrl } from '../lib/utils';
import { getCoordinatesFromGroq } from '../lib/groq';
import toast from 'react-hot-toast';
import styles from './AddSpotModal.module.css';

const MAX_SIZE_MB = 2;

export default function AddSpotModal({ onClose, onSuccess, initialData }) {
  const { user } = useAuth();
  const isEdit = !!initialData;
  const [loading, setLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  const [photos, setPhotos] = useState([null, null, null]);
  const [form, setForm] = useState({
    name: initialData?.name || '',
    address: initialData?.address || '',
    maps_link: '',
    latitude: initialData?.latitude || '',
    longitude: initialData?.longitude || '',
    opens_at: initialData?.opens_at || '06:00',
    closes_at: initialData?.closes_at || '22:00',
    chai_price: initialData?.chai_price || '',
    phone: initialData?.phone || '',
    description: initialData?.description || '',
  });

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const validate = () => {
    if (!form.name.trim()) {
      toast.error('Please enter the spot name');
      return false;
    }
    if (!form.address.trim()) {
      toast.error('Please enter the address');
      return false;
    }
    if (!form.opens_at) {
      toast.error('Please enter opening time');
      return false;
    }
    if (!form.closes_at) {
      toast.error('Please enter closing time');
      return false;
    }
    if (!form.chai_price) {
      toast.error('Please enter the chai price');
      return false;
    }
    if (!form.latitude || !form.longitude) {
      toast.error('Please paste a Google Maps link so we can find the location');
      return false;
    }
    if (!photos.some(Boolean)) {
      toast.error('Please upload at least one photo');
      return false;
    }
    return true;
  };

  const handleMapsLink = async (url) => {
    set('maps_link', url);
    if (!url || url.length < 10) return;

    const coords = parseGoogleMapsUrl(url);
    if (coords) {
      set('latitude', coords.lat);
      set('longitude', coords.lng);
      toast.success('Location extracted!');
      return;
    }

    setLocating(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/expand-url`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ url }),
        }
      );

      const json = await res.json();
      const expandedUrl = json.finalUrl || '';
      const text = json.html || '';

      const expandedCoords = parseGoogleMapsUrl(expandedUrl);
      if (expandedCoords) {
        set('latitude', expandedCoords.lat);
        set('longitude', expandedCoords.lng);
        toast.success('Location extracted!');
        setLocating(false);
        return;
      }

      const htmlMatch =
        text.match(/\/@(-?\d+\.\d+),(-?\d+\.\d+)/) ||
        text.match(/!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/) ||
        text.match(/"(-?\d{1,3}\.\d{4,}),(-?\d{1,3}\.\d{4,})"/);

      if (htmlMatch) {
        set('latitude', parseFloat(htmlMatch[1]));
        set('longitude', parseFloat(htmlMatch[2]));
        toast.success('Location extracted!');
        setLocating(false);
        return;
      }
    } catch (err) {
      console.error('edge function failed:', err);
    }

    if (form.name || form.address) {
      try {
        const groqCoords = await getCoordinatesFromGroq(form.name, form.address);
        if (groqCoords) {
          set('latitude', groqCoords.lat);
          set('longitude', groqCoords.lng);
          toast.success('Location found with AI!');
          setLocating(false);
          return;
        }
      } catch (err) {
        console.error('Groq failed:', err);
      }
    }

    setLocating(false);
  };

  const handlePhoto = (idx, file) => {
    if (!file) return;
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      toast.error(`Photo must be under ${MAX_SIZE_MB}MB`);
      return;
    }
    const updated = [...photos];
    updated[idx] = file;
    setPhotos(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) { toast.error('Please sign in first'); return; }
    if (!validate()) return;

    setLoading(true);

    let lat = form.latitude ? parseFloat(form.latitude) : null;
    let lng = form.longitude ? parseFloat(form.longitude) : null;

    if (!lat || !lng) {
      try {
        const groqCoords = await getCoordinatesFromGroq(form.name, form.address);
        if (groqCoords) {
          lat = groqCoords.lat;
          lng = groqCoords.lng;
        }
      } catch (err) {
        console.error('Groq failed:', err);
      }
    }

    const payload = {
      name: form.name,
      address: form.address,
      latitude: lat,
      longitude: lng,
      opens_at: form.opens_at || null,
      closes_at: form.closes_at || null,
      chai_price: form.chai_price ? parseFloat(form.chai_price) : null,
      phone: form.phone || null,
      description: form.description || null,
      created_by: user.id,
    };

    let spot;

    if (isEdit) {
      const { data, error } = await updateSpot(initialData.id, payload);
      if (error) { toast.error(error.message); setLoading(false); return; }
      spot = data;
    } else {
      const { data, error } = await createSpot(payload);
      if (error) { toast.error(error.message); setLoading(false); return; }
      spot = data;
    }

    const photoUploads = photos
      .filter(Boolean)
      .map((file, i) => uploadSpotPhoto(spot.id, file, i));
    await Promise.all(photoUploads);

    toast.success(isEdit ? 'Spot updated!' : 'Spot added!');
    setLoading(false);
    onSuccess?.();
    onClose();
  };

  return (
    <div className={styles.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className={styles.sheet}>
        <div className={styles.sheetHeader}>
          <div>
            <h2 className={styles.title}>{isEdit ? 'Edit Spot' : 'Add a Chaya Spot'}</h2>
            <p className={styles.sub}>Help the community find great Chaya</p>
          </div>
          <button className={styles.close} onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>

          <p style={{ fontSize: '12px', color: 'var(--muted)', textAlign: 'center' }}>
            Fields marked <span style={{ color: '#e07070' }}>*</span> are required
          </p>

          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Basic info</h3>
            <div className={styles.field}>
              <label className={styles.label}>Spot name <span style={{ color: '#e07070' }}>*</span></label>
              <input className={styles.input} type="text" placeholder="e.g. Basheer Ikka de Thattukada" value={form.name} onChange={e => set('name', e.target.value)} />
            </div>
            <div className={styles.row}>
              <div className={styles.field}>
                <label className={styles.label}>Chaya price (Rs.) <span style={{ color: '#e07070' }}>*</span></label>
                <input className={styles.input} type="number" placeholder="8" min="0" value={form.chai_price} onChange={e => set('chai_price', e.target.value)} />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Contact number <span style={{ color: 'var(--muted)', fontSize: '10px' }}>(optional)</span></label>
                <input className={styles.input} type="tel" placeholder="+91 98..." value={form.phone} onChange={e => set('phone', e.target.value)} />
              </div>
            </div>
            <div className={styles.row}>
              <div className={styles.field}>
                <label className={styles.label}>Opens at <span style={{ color: '#e07070' }}>*</span></label>
                <input className={styles.input} type="time" value={form.opens_at} onChange={e => set('opens_at', e.target.value)} />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Closes at <span style={{ color: '#e07070' }}>*</span></label>
                <input className={styles.input} type="time" value={form.closes_at} onChange={e => set('closes_at', e.target.value)} />
              </div>
            </div>
          </section>

          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Location</h3>
            <div className={styles.field}>
              <label className={styles.label}>Address <span style={{ color: '#e07070' }}>*</span></label>
              <input className={styles.input} type="text" placeholder="Beach Road, Kozhikode, Kerala" value={form.address} onChange={e => set('address', e.target.value)} />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>
                Google Maps link <span style={{ color: '#e07070' }}>*</span>
                {locating && (
                  <span style={{ marginLeft: '8px', fontSize: '11px', color: 'var(--orange-light)', fontWeight: 500 }}>
                    finding location...
                  </span>
                )}
              </label>
              <input
                className={styles.input}
                type="text"
                placeholder="Paste any Google Maps link"
                value={form.maps_link}
                onChange={e => handleMapsLink(e.target.value)}
              />
              {form.latitude && form.longitude && (
                <span style={{ fontSize: '11px', color: '#7ecb50', marginTop: '4px', display: 'block' }}>
                  Location found ({parseFloat(form.latitude).toFixed(4)}, {parseFloat(form.longitude).toFixed(4)})
                </span>
              )}
            </div>
          </section>

          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>
              Photos <span style={{ color: '#e07070' }}>* at least 1</span>
              <span className={styles.hint}> (max 3, under 2MB each)</span>
            </h3>
            <div className={styles.photoGrid}>
              {[0, 1, 2].map(i => (
                <label key={i} className={styles.photoSlot}>
                  {photos[i] ? (
                    <img src={URL.createObjectURL(photos[i])} alt="" className={styles.photoPreview} />
                  ) : (
                    <div className={styles.photoEmpty}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <rect x="3" y="3" width="18" height="18" rx="2"/>
                        <circle cx="8.5" cy="8.5" r="1.5"/>
                        <polyline points="21 15 16 10 5 21"/>
                      </svg>
                      <span>Photo {i + 1}</span>
                    </div>
                  )}
                  <input type="file" accept="image/*" className={styles.fileInput} onChange={e => handlePhoto(i, e.target.files[0])} />
                </label>
              ))}
            </div>
          </section>

          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Description <span className={styles.hint}>(optional)</span></h3>
            <textarea
              className={`${styles.input} ${styles.textarea}`}
              rows={3}
              placeholder="e.g. Chill spot near the beach, perfect for evenings. Great view and local vibe."
              value={form.description}
              onChange={e => set('description', e.target.value)}
            />
          </section>

          <button type="submit" className={styles.submit} disabled={loading || locating}>
            {loading ? 'Saving...' : isEdit ? 'Save changes' : '+ Submit spot'}
          </button>
        </form>
      </div>
    </div>
  );
}